import { FC, useState } from 'react';

import {
  Text,
  Flex,
  Image,
  Input,
  Button,
  Center,
  Spinner,
  useToast,
  SimpleGrid,
  IconButton,
} from '@chakra-ui/react';

import { motion } from 'framer-motion';
import { MdEdit } from 'react-icons/md';

import { useMutation, useQueryClient } from 'react-query';

import { selectors } from 'store/slices/washhub';

import { editHarvestCounts } from 'api/pickup';
import { Pickup, packages } from 'types/pickup';
import { CustomTable } from 'components/modules/CustomTable';

type Props = {
  isLoading: boolean;
  pickup?: Pickup;
};

const MotionImage = motion(Image);

export const HarvestCounts: FC<Props> = ({ isLoading, pickup }) => {
  const [selected, setSelected] = useState('');
  // Counts are read-only until the user explicitly enters edit mode via the
  // pencil icon (mirrors the Breakdown KPI tab) — too easy to edit by accident
  // otherwise.
  const [isEditing, setIsEditing] = useState(false);
  // Local overrides while editing, keyed by item SKU. Cleared on save.
  const [edits, setEdits] = useState<{
    [sku: string]: { openedQuantity?: number; sealedQuantity?: number };
  }>({});

  const toast = useToast();
  const queryClient = useQueryClient();
  const washHubId = selectors.useSelectedWashHubId();

  const { mutate: saveCounts, isLoading: isSaving } = useMutation(
    editHarvestCounts,
    {
      onSuccess: () => {
        toast({ title: 'Harvest counts updated', status: 'success' });

        queryClient.invalidateQueries([`fetch-pickups-ById`, pickup?._id]);
        queryClient.invalidateQueries(`fetch-pickups-${washHubId}`);
        queryClient.invalidateQueries(
          `fetch-pickups-status-count-${washHubId}`,
        );

        setEdits({});
        setIsEditing(false);
      },
      onError: () => {
        toast({ title: 'Could not save harvest counts', status: 'error' });
      },
    },
  );

  let Countpictures = pickup?.proofOfHarvestFilesImageUrls || [];

  if (isLoading) {
    return (
      <Center flex={1} p="50px">
        <Spinner size="lg" />
      </Center>
    );
  }
  const OpenedPackages = pickup?.pickedUpStats ?? [];
  const SealedPackages = pickup?.unusedPackages ?? [];
  // Admin-edited item-level unused counts; when present they override the
  // package-derived numbers (same rule the API uses for reports/exports).
  const UnusedItemStats = pickup?.unusedItemStats ?? [];
  // Initialize totalPackages array as an object to keep track of quantities by skuId
  const totalPackagesMap: { [sku: string]: any } = {};
  // Helper function to add or update package quantities in totalPackagesMap
  const addOrUpdatePackage = (pkg: packages, source: string) => {
    const { sku, quantity, ...rest } = pkg;

    if (source === 'opened' && sku !== undefined) {
      if (!totalPackagesMap[sku] && sku !== undefined) {
        totalPackagesMap[sku] = {
          sku,
          openedQuantity: 0,
          sealedQuantity: 0,
          ...rest,
        };
      }
      totalPackagesMap[sku].openedQuantity += quantity;
    } else if (source === 'sealed' && sku !== undefined) {
      let totalQuantity = 0;
      for (const items of rest.skuDetail?.items) {
        if (!totalPackagesMap[items.sku] && items.sku !== undefined) {
          // Row is keyed (and saved) by the ITEM sku; the package skuDetail is
          // only kept as a display fallback for the name.
          totalPackagesMap[items.sku] = {
            sku: items.sku,
            openedQuantity: 0,
            sealedQuantity: 0,
            ...rest,
          };
        }
        totalQuantity += items.quantity;
        totalPackagesMap[items.sku].sealedQuantity += quantity * totalQuantity;
      }
    } else if (source === 'unusedItems' && sku !== undefined) {
      if (!totalPackagesMap[sku]) {
        totalPackagesMap[sku] = {
          sku,
          openedQuantity: 0,
          sealedQuantity: 0,
          ...rest,
        };
      }
      totalPackagesMap[sku].sealedQuantity += quantity;
    }
  };

  // Process OpenedPackages
  OpenedPackages.forEach((pkg) => addOrUpdatePackage(pkg, 'opened'));

  // Process unused counts: item-level admin edits win over package derivation.
  if (UnusedItemStats.length > 0) {
    UnusedItemStats.forEach((pkg) => addOrUpdatePackage(pkg, 'unusedItems'));
  } else {
    SealedPackages.forEach((pkg) => addOrUpdatePackage(pkg, 'sealed'));
  }

  // Convert totalPackagesMap to an array
  const totalPackages = Object.values(totalPackagesMap);

  // Resolve the effective count for a row, preferring the local edit when set.
  const effectiveOpened = (pkg: any) => {
    const edited = edits[pkg.sku]?.openedQuantity;
    if (edited !== undefined) return edited;
    return isNaN(pkg.openedQuantity) ? 0 : pkg.openedQuantity;
  };
  const effectiveSealed = (pkg: any) => {
    const edited = edits[pkg.sku]?.sealedQuantity;
    if (edited !== undefined) return edited;
    return isNaN(pkg.sealedQuantity) ? 0 : pkg.sealedQuantity;
  };

  const setEdit = (
    sku: string,
    field: 'openedQuantity' | 'sealedQuantity',
    value: string,
  ) => {
    const parsed = value === '' ? 0 : Number(value);
    if (isNaN(parsed) || parsed < 0) return;
    setEdits((prev) => ({
      ...prev,
      [sku]: { ...prev[sku], [field]: parsed },
    }));
  };

  const onSave = () => {
    if (!pickup?._id) return;
    saveCounts({
      pickupId: pickup._id,
      counts: totalPackages.map((pkg) => ({
        sku: String(pkg.sku),
        dirtyQuantity: effectiveOpened(pkg),
        unusedQuantity: effectiveSealed(pkg),
      })),
    });
  };

  const totalSealedPackages = totalPackages.reduce(
    (acc, pkg) => acc + effectiveSealed(pkg),
    0,
  );
  const totalOpenedPackages = totalPackages.reduce(
    (acc, pkg) => acc + effectiveOpened(pkg),
    0,
  );
  const tableHeaders = ['Item SKU', 'Dirty & Open', 'Unused', 'Total Items'];

  const renderCount = (
    row: any,
    field: 'openedQuantity' | 'sealedQuantity',
    value: number,
  ) => {
    if (!isEditing) {
      return (
        <Text flex={1} textAlign="center">
          {value}
        </Text>
      );
    }
    return (
      <Flex flex={1} justify="center">
        <Input
          size="sm"
          type="number"
          min={0}
          w="80px"
          textAlign="center"
          value={value}
          onChange={(e) => setEdit(row.sku, field, e.target.value)}
        />
      </Flex>
    );
  };

  return (
    <Flex w="100%" mt="20px" flex={1} gap="20px" fontSize="14px">
      <Flex direction="column" width="70%">
        <Flex justify="flex-end">
          {isEditing ? (
            <Button
              size="sm"
              isLoading={isSaving}
              onClick={onSave}
              isDisabled={totalPackages.length === 0}>
              Save
            </Button>
          ) : (
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Edit harvest counts"
              icon={<MdEdit size="18px" />}
              onClick={() => setIsEditing(true)}
            />
          )}
        </Flex>
        <CustomTable
          mt="10px"
          type="orders"
          minW="100%"
          maxH="360px"
          fixedHeaderRow
          fixedFooterRow
          data={totalPackages ?? []}
          isLoading={isLoading}
          isSelectable={false}>
          {{
            headerRow: (
              <Flex width="100%">
                {tableHeaders.map((header, index) => (
                  <Text key={index} flex={1} textAlign="center">
                    {header}
                  </Text>
                ))}
              </Flex>
            ),
            renderRow: (pickup) => {
              const openedQuantity = effectiveOpened(pickup);
              const sealedQuantity = effectiveSealed(pickup);

              return (
                <Flex width="100%" align="center">
                  <Text flex={1} textAlign="center">
                    {pickup?.skuDetail?.name ?? ''}
                  </Text>
                  {renderCount(pickup, 'openedQuantity', openedQuantity)}
                  {renderCount(pickup, 'sealedQuantity', sealedQuantity)}
                  <Text flex={1} textAlign="center">
                    {openedQuantity + sealedQuantity}
                  </Text>
                </Flex>
              );
            },
            renderEmpty: () => (
              <Center p={8}>
                <Text fontSize="20px">
                  Harvest count has not been entered yet
                </Text>
              </Center>
            ),
            footerRow: (
              <Flex width="100%" mt="10px">
                <Text flex={1} textAlign="center" fontWeight="bold">
                  Total
                </Text>
                <Text flex={1} textAlign="center" fontWeight="bold">
                  {totalOpenedPackages}
                </Text>
                <Text flex={1} textAlign="center" fontWeight="bold">
                  {totalSealedPackages}
                </Text>
                <Text flex={1} textAlign="center" fontWeight="bold">
                  {' '}
                </Text>
              </Flex>
            ),
          }}
        </CustomTable>
      </Flex>
      <Flex direction="column" gap="20px" w="100%">
        <Flex direction="row" gap="20px" width="70%" marginTop={'10px'}>
          {!isLoading ? (
            <SimpleGrid flex={1} columns={{ base: 2, md: 3 }} spacing="10px">
              {Countpictures.map((p) => (
                <Image
                  key={p}
                  src={p}
                  objectFit="cover"
                  h="100px"
                  w="100px"
                  borderRadius="10px"
                  overflow="hidden"
                  draggable={false}
                  onClick={() => {
                    setSelected((prev) => (prev === p ? '' : p));
                  }}
                  cursor="pointer"
                  opacity={selected === p ? 0.5 : 1}
                />
              ))}
            </SimpleGrid>
          ) : (
            <Center p="50px">
              <Text fontWeight="500" color="#959595">
                No Pictures uploaded
              </Text>
            </Center>
          )}
        </Flex>
        <Flex direction="column" gap="20px" width="100%">
          {Countpictures.length > 0 && (
            <Center flex={1}>
              <Center
                bg="#f1f1f1"
                h="300px"
                w="300px"
                borderRadius="10px"
                overflow="hidden">
                {Boolean(selected) ? (
                  <MotionImage
                    src={selected}
                    objectFit="contain"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  />
                ) : (
                  <Text>Select a picture to preview</Text>
                )}
              </Center>
            </Center>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
